import { z } from 'zod'

import { onboardingStatusSchema } from './onboarding-contracts'

const readCapabilitySchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.literal('VERIFIED'),
    operation: z.literal('APPOINTMENT_TYPES_READ'),
    locationId: z.number().int().positive(),
    resourceCount: z.number().int().nonnegative(),
    resultFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict()

const resolvedLocationIdentitySchema = z
  .object({
    locationId: z.number().int().positive(),
    locationName: z.string().trim().min(1),
    address: z.string().trim().min(1).nullable(),
    timeZone: z.string().trim().min(1).optional(),
    institutionName: z.string().trim().min(1),
    subdomain: z.string().trim().min(1),
    pms: z.string().trim().min(1),
    readStatus: z.enum(['GREEN', 'RED', 'UNKNOWN']),
    writeStatus: z.enum(['GREEN', 'RED', 'UNKNOWN']),
    syncHealth: z.enum(['ACTIVE', 'DEGRADED', 'UNKNOWN']),
    readCapability: readCapabilitySchema,
    workspaceReadiness: z.literal('READY'),
    verificationFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.readCapability.locationId !== value.locationId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'read capability location does not match',
      })
    }
    const expectedSyncHealth =
      value.readStatus === 'GREEN' && value.writeStatus === 'GREEN'
        ? 'ACTIVE'
        : value.readStatus === 'RED' || value.writeStatus === 'RED'
          ? 'DEGRADED'
          : 'UNKNOWN'
    if (value.syncHealth !== expectedSyncHealth) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'sync health does not match raw status evidence',
      })
    }
  })

export const resolvedNexHealthLocationSchema = resolvedLocationIdentitySchema

const clinicSchema = z
  .object({
    tenantUid: z.string().trim().min(1).max(128),
    name: z.string().trim().min(1),
    dashboardReady: z.boolean(),
    onboardingStatus: onboardingStatusSchema.nullable(),
    communicationsStatus: z.enum([
      'NOT_CONFIGURED',
      'IN_PROGRESS',
      'NEEDS_ATTENTION',
      'ACTIVE',
    ]),
  })
  .strict()

export const organizationResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    organization: z
      .object({ id: z.string().trim().min(1), name: z.string().trim().min(1) })
      .strict(),
    role: z.enum(['OWNER', 'ADMIN']),
    canAddClinic: z.boolean(),
    clinics: z.array(clinicSchema),
    activeDraft: z
      .object({
        tenantUid: z.string().trim().min(1).max(128),
        status: onboardingStatusSchema,
        revision: z.number().int().positive(),
        resolvedLocation: resolvedNexHealthLocationSchema,
      })
      .strict()
      .nullable(),
  })
  .strict()

const managerAccessSchema = z
  .object({
    targetUid: z.string().trim().min(1).max(128),
    email: z.string().email().nullable(),
    tenantUids: z.array(z.string().trim().min(1).max(128)),
    tenantUid: z.string().trim().min(1).max(128).optional(),
    membershipStatus: z.enum(['ACTIVE', 'REVOKED']),
    membershipRevision: z.number().int().positive(),
    managerLifecycleStatus: z.enum(['ACTIVE', 'REVOKED']),
    managerLifecycleRevision: z.number().int().positive(),
  })
  .strict()
  .superRefine((value, context) => {
    const active = value.managerLifecycleStatus === 'ACTIVE'
    if (active !== (value.membershipStatus === 'ACTIVE')) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'manager membership and lifecycle disagree',
      })
    }
    const uniqueTenantUids = new Set(value.tenantUids)
    if (uniqueTenantUids.size !== value.tenantUids.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'manager clinic grants must be unique',
      })
    }
    if (active !== value.tenantUids.length > 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'manager clinic binding is inconsistent',
      })
    }
    if (
      (value.tenantUids.length === 1) !== (value.tenantUid !== undefined) ||
      (value.tenantUid !== undefined && value.tenantUid !== value.tenantUids[0])
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'manager singleton clinic binding is inconsistent',
      })
    }
  })

const managerGrantEvidenceSchema = z
  .object({
    organizationId: z.string().trim().min(1),
    tenantUid: z.string().trim().min(1).max(128),
    targetUid: z.string().trim().min(1).max(128),
    operationId: z.string().trim().min(1).max(128),
    membershipRevision: z.number().int().positive(),
    managerLifecycleRevision: z.number().int().positive(),
  })
  .strict()

export const managerInvitationSchema = z
  .object({
    schemaVersion: z.literal(1),
    invitationId: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
    organizationId: z.string().trim().min(1),
    tenantUid: z.string().trim().min(1).max(128),
    email: z.string().email(),
    capability: z.literal('CLINIC_MANAGER'),
    status: z.enum(['ACTIVE', 'CLAIMING', 'CLAIMED', 'REVOKED']),
    revision: z.number().int().positive(),
    expiresAtMs: z.number().int().nonnegative(),
    deliveryStatus: z.enum(['PENDING', 'ACCEPTED', 'REJECTED', 'AMBIGUOUS']),
    expired: z.boolean().optional(),
    grant: managerGrantEvidenceSchema.optional(),
  })
  .strict()

// The one step that gives the invitee a signed-in account for the invited
// email, reported only to the emailed link's holder. Absent when the server
// predates it or could not look it up.
export const invitationAccountSetupSchema = z.enum([
  'CREATE_ACCOUNT',
  'SIGN_IN',
  'SET_PASSWORD',
  'UNAVAILABLE',
])

export const managerInvitationPreviewSchema = managerInvitationSchema
  .extend({
    clinicName: z.string().trim().min(1).max(120),
    accountSetup: invitationAccountSetupSchema.optional(),
  })
  .strict()

export const organizationAccessManagementSchema = z
  .object({
    schemaVersion: z.literal(1),
    organizationId: z.string().trim().min(1),
    managers: z.array(managerAccessSchema),
    invitations: z.array(managerInvitationSchema),
  })
  .strict()

export const managerLifecycleResultSchema = z
  .object({
    schemaVersion: z.literal(1),
    organizationId: z.string().trim().min(1),
    targetUid: z.string().trim().min(1).max(128),
    membershipRole: z.literal('MEMBER'),
    membershipStatus: z.enum(['ACTIVE', 'REVOKED']),
    membershipRevision: z.number().int().positive(),
    principalType: z.literal('NON_OWNER'),
    clinicAccess: z.enum([
      'EXACT_ONE_BRANCH',
      'EXACT_MULTIPLE_BRANCHES',
      'NONE',
    ]),
    managerLifecycleStatus: z.enum(['ACTIVE', 'REVOKED']),
    managerLifecycleRevision: z.number().int().positive(),
    lastOperationId: z.string().trim().min(1).max(128),
    tenantUids: z.array(z.string().trim().min(1).max(128)),
    tenantUid: z.string().trim().min(1).max(128).optional(),
    outcome: z.enum([
      'COMMITTED',
      'COMMITTED_AFTER_AMBIGUOUS_RESPONSE',
      'EXACT_REPLAY',
      'STATE_EQUIVALENT',
    ]),
  })
  .strict()
  .superRefine((value, context) => {
    const uniqueTenantUids = new Set(value.tenantUids)
    const active = value.managerLifecycleStatus === 'ACTIVE'
    const expectedClinicAccess =
      value.tenantUids.length === 0
        ? 'NONE'
        : value.tenantUids.length === 1
          ? 'EXACT_ONE_BRANCH'
          : 'EXACT_MULTIPLE_BRANCHES'
    if (
      uniqueTenantUids.size !== value.tenantUids.length ||
      active !== (value.membershipStatus === 'ACTIVE') ||
      active !== value.tenantUids.length > 0 ||
      value.clinicAccess !== expectedClinicAccess ||
      (value.tenantUids.length === 1) !== (value.tenantUid !== undefined) ||
      (value.tenantUid !== undefined && value.tenantUid !== value.tenantUids[0])
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'manager lifecycle result is inconsistent',
      })
    }
  })

export const claimedManagerInvitationSchema = managerInvitationSchema.extend({
  status: z.literal('CLAIMED'),
  grant: managerGrantEvidenceSchema,
})

// Organization Administrator invitations share the invitation lifecycle and
// claim link; they are Organization-wide and name no clinic.
export const administratorGrantEvidenceSchema = z
  .object({
    organizationId: z.string().trim().min(1),
    targetUid: z.string().trim().min(1).max(128),
    operationId: z.string().trim().min(1).max(128),
    membershipRevision: z.number().int().positive(),
  })
  .strict()

export const administratorInvitationSchema = z
  .object({
    schemaVersion: z.literal(1),
    invitationId: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
    organizationId: z.string().trim().min(1),
    email: z.string().email(),
    capability: z.literal('ORGANIZATION_ADMIN'),
    status: z.enum(['ACTIVE', 'CLAIMING', 'CLAIMED', 'REVOKED']),
    revision: z.number().int().positive(),
    expiresAtMs: z.number().int().nonnegative(),
    deliveryStatus: z.enum(['PENDING', 'ACCEPTED', 'REJECTED', 'AMBIGUOUS']),
    expired: z.boolean().optional(),
    grant: administratorGrantEvidenceSchema.optional(),
  })
  .strict()

export const administratorInvitationPreviewSchema =
  administratorInvitationSchema
    .extend({
      organizationName: z.string().trim().min(1).max(120),
      accountSetup: invitationAccountSetupSchema.optional(),
    })
    .strict()

export const invitationPreviewSchema = z.discriminatedUnion('capability', [
  managerInvitationPreviewSchema,
  administratorInvitationPreviewSchema,
])

export const claimedAdministratorInvitationSchema =
  administratorInvitationSchema.extend({
    status: z.literal('CLAIMED'),
    grant: administratorGrantEvidenceSchema,
  })

export const claimedInvitationSchema = z.discriminatedUnion('capability', [
  claimedManagerInvitationSchema,
  claimedAdministratorInvitationSchema,
])

export const organizationAdministratorSchema = z
  .object({
    targetUid: z.string().trim().min(1).max(128),
    role: z.enum(['OWNER', 'ADMIN']),
    membershipRevision: z.number().int().positive(),
    email: z.string().email().nullable(),
  })
  .strict()

export const administratorAccessSchema = z
  .object({
    schemaVersion: z.literal(1),
    organizationId: z.string().trim().min(1),
    administrators: z.array(organizationAdministratorSchema),
    invitations: z.array(administratorInvitationSchema),
  })
  .strict()

export const confirmedOrganizationClinicSchema = clinicSchema

export const organizationCommunicationsSchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantUid: z.string().trim().min(1).max(128),
    status: z.enum([
      'NOT_CONFIGURED',
      'SETUP_REQUIRED',
      'PROVISIONING',
      'VALIDATING',
      'READY',
      'PARTIAL',
      'BLOCKED',
    ]),
    reason: z.string().nullable(),
    revision: z.number().int().positive(),
    progress: z.string().nullable(),
    canRetry: z.boolean(),
    capability: z
      .enum(['TEXT_ONLY', 'VOICE_ONLY', 'VOICE_AND_TEXT'])
      .nullable(),
    publicNumber: z.string().nullable(),
    mainPhone: z.string().nullable(),
    availableCapabilities: z
      .object({ text: z.boolean(), voice: z.boolean() })
      .strict(),
    readiness: z
      .object({
        text: z.enum(['READY', 'NOT_READY']),
        voice: z.enum(['READY', 'NOT_READY']),
      })
      .strict(),
    maxNumbers: z.literal(1),
    recovery: z
      .object({
        planId: z.string().regex(/^[0-9a-f]{64}$/),
        action: z.literal('RESTORE_PREVIOUS_ROUTING'),
      })
      .strict()
      .nullable(),
  })
  .strict()

const setupPrerequisiteStatusSchema = z.enum([
  'COMPLETE',
  'REQUIRED',
  'NOT_APPLICABLE',
])

// Read-only Step 2 prerequisite groups. Clinic Info and Working Hours gate
// every capability; Services and Booking gate Voice only.
export const organizationCommunicationsPrerequisitesSchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantUid: z.string().trim().min(1).max(128),
    prerequisites: z
      .object({
        clinicInfo: setupPrerequisiteStatusSchema,
        workingHours: setupPrerequisiteStatusSchema,
        services: setupPrerequisiteStatusSchema,
        booking: setupPrerequisiteStatusSchema,
      })
      .strict(),
  })
  .strict()

export const organizationCommunicationsRecoverySchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantUid: z.string().trim().min(1).max(128),
    planId: z.string().regex(/^[0-9a-f]{64}$/),
    status: z.literal('ROLLED_BACK'),
    providerReadback: z.literal('MATCH'),
    configurationCommandState: z.literal('SUCCEEDED'),
    nextCommand: z
      .object({
        operationKind: z.string().trim().min(1),
        status: z.string().trim().min(1),
      })
      .strict(),
    safeToResume: z.literal(true),
  })
  .strict()

export type OrganizationResponse = z.infer<typeof organizationResponseSchema>
export type OrganizationAccessManagement = z.infer<
  typeof organizationAccessManagementSchema
>
export type ManagerAccess = OrganizationAccessManagement['managers'][number]
export type ManagerInvitation = z.infer<typeof managerInvitationSchema>
export type ManagerInvitationPreview = z.infer<
  typeof managerInvitationPreviewSchema
>
export type ManagerLifecycleResult = z.infer<
  typeof managerLifecycleResultSchema
>
export type AdministratorInvitation = z.infer<
  typeof administratorInvitationSchema
>
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>
export type InvitationAccountSetup = z.infer<
  typeof invitationAccountSetupSchema
>
export type ClaimedInvitation = z.infer<typeof claimedInvitationSchema>
export type OrganizationAdministrator = z.infer<
  typeof organizationAdministratorSchema
>
export type AdministratorAccess = z.infer<typeof administratorAccessSchema>
export type ClaimedManagerInvitation = z.infer<
  typeof claimedManagerInvitationSchema
>
export type ResolvedNexHealthLocation = z.infer<
  typeof resolvedNexHealthLocationSchema
>
export type ConfirmedOrganizationClinic = z.infer<
  typeof confirmedOrganizationClinicSchema
>
export type OrganizationCommunications = z.infer<
  typeof organizationCommunicationsSchema
>
export type OrganizationCommunicationsPrerequisites = z.infer<
  typeof organizationCommunicationsPrerequisitesSchema
>
export type OrganizationCommunicationsRecovery = z.infer<
  typeof organizationCommunicationsRecoverySchema
>

export const twilioConnectionSchema = z
  .object({
    tenantUid: z.string().min(1).max(128),
    status: z.enum([
      'SETUP_REQUIRED',
      'PROVISIONING',
      'CONNECTED',
      'NEEDS_REPAIR',
      'BLOCKED',
    ]),
    publicNumber: z.string().nullable(),
    checkedAtMs: z.number().int(),
    voiceMatch: z.boolean().nullable(),
    smsMatch: z.boolean().nullable(),
    routeMatch: z.boolean().nullable(),
    reason: z.string().nullable(),
  })
  .strict()
export type TwilioConnection = z.infer<typeof twilioConnectionSchema>

export const clinicAdoptionSchema = z
  .object({
    operationId: z.string().min(1),
    approvalFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    tenantUid: z.string().min(1),
    organizationId: z.string().min(1),
    clinicName: z.string().min(1),
    organizationName: z.string().min(1),
    recipientUids: z.array(z.string().min(1)).min(1),
    onboardingStatus: onboardingStatusSchema,
    status: z.enum(['PREVIEW', 'ALREADY_LINKED', 'ADOPTED']),
  })
  .strict()
export type ClinicAdoption = z.infer<typeof clinicAdoptionSchema>
